import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  Search,
  Edit2,
  Eye,
  FileText,
  Download,
  Send,
  CheckCircle2,
  Trash2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { invoicesAPI, downloadFile } from '../../services/api';
import { useCompany } from '../../services/companyState';
import { formatEuro } from '../../services/serviceCatalog';
import LoadingSpinner from '../Common/LoadingSpinner';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

const STATUS_LABELS = {
  draft: 'Entwurf',
  sent: 'Versendet',
  paid: 'Bezahlt',
  overdue: 'Überfällig',
};
const STATUS_CLASS = {
  draft: 'status-badge status-draft',
  sent: 'status-badge status-sent',
  paid: 'status-badge status-paid',
  overdue: 'status-badge status-overdue',
};

const Invoices = () => {
  const company = useCompany();
  const isKleinunternehmer = company?.small_business === true;
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    loadInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, statusFilter]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const response = await invoicesAPI.getAll({
        search: searchTerm || undefined,
        status: statusFilter || undefined,
        limit: 100,
      });
      setInvoices(response.data);
    } catch (err) {
      console.error(err);
      toast.error('Fehler beim Laden der Rechnungen');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (s) => format(parseISO(s), 'dd.MM.yyyy', { locale: de });

  const handleDownloadPdf = async (inv) => {
    try {
      setBusyId(inv.id);
      if (!inv.pdf_path) {
        await invoicesAPI.generatePDF(inv.id);
      }
      const res = await invoicesAPI.download(inv.id);
      downloadFile(res.data, `Rechnung_${inv.invoice_number}.pdf`);
    } catch {
      toast.error('PDF-Download fehlgeschlagen');
    } finally {
      setBusyId(null);
    }
  };

  const handleSend = async (inv) => {
    if (!inv.customer?.email) {
      toast.error('Kunde hat keine E-Mail');
      return;
    }
    if (!window.confirm(`Rechnung an ${inv.customer.email} senden?`)) return;
    try {
      setBusyId(inv.id);
      await invoicesAPI.send(inv.id);
      toast.success('Versand eingeleitet');
      loadInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Versand fehlgeschlagen');
    } finally {
      setBusyId(null);
    }
  };

  const handleMarkPaid = async (inv) => {
    if (!window.confirm(`Rechnung ${inv.invoice_number} als bezahlt markieren?`)) return;
    try {
      setBusyId(inv.id);
      await invoicesAPI.markPaid(inv.id);
      toast.success('Als bezahlt markiert');
      loadInvoices();
    } catch {
      toast.error('Fehler');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (inv) => {
    if (!window.confirm(`Rechnung ${inv.invoice_number} wirklich löschen?`)) return;
    try {
      setBusyId(inv.id);
      await invoicesAPI.delete(inv.id);
      toast.success('Gelöscht');
      loadInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Löschen fehlgeschlagen');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <div className="content-header">
        <div>
          <h1>Rechnungen</h1>
          <p>Rechnungsnummer automatisch (RE-JJJJ-NNNN){isKleinunternehmer ? ' · §19 UStG' : ''}</p>
        </div>
        <Link to="/invoices/new" className="btn btn-primary">
          <Plus size={16} />
          Neue Rechnung
        </Link>
      </div>

      <div className="content-body">
        <div className="card mb-6">
          <div className="card-body">
            <div className="flex gap-4 items-center">
              <div className="flex-1">
                <div className="relative">
                  <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Rechnungen suchen..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="form-input pl-10"
                  />
                </div>
              </div>
              <div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="form-input"
                >
                  <option value="">Alle Status</option>
                  <option value="draft">Entwurf</option>
                  <option value="sent">Versendet</option>
                  <option value="paid">Bezahlt</option>
                  <option value="overdue">Überfällig</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-8"><LoadingSpinner size="large" /></div>
        ) : (
          <div className="table-container">
            {invoices.length > 0 ? (
              <table className="table">
                <thead>
                  <tr>
                    <th>Nr.</th>
                    <th>Datum</th>
                    <th>Kunde</th>
                    <th>Titel</th>
                    <th>Fällig</th>
                    <th className="text-right">Betrag</th>
                    <th>Status</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => {
                    const busy = busyId === inv.id;
                    return (
                      <tr key={inv.id}>
                        <td className="font-semibold">{inv.invoice_number}</td>
                        <td>{formatDate(inv.invoice_date)}</td>
                        <td>{inv.customer?.name || '—'}</td>
                        <td>{inv.title || <span className="text-gray-400">—</span>}</td>
                        <td>{formatDate(inv.due_date)}</td>
                        <td className="text-right">{formatEuro(inv.net_amount)}</td>
                        <td>
                          <span className={STATUS_CLASS[inv.status]}>
                            {STATUS_LABELS[inv.status] || inv.status}
                          </span>
                        </td>
                        <td>
                          <div className="table-actions">
                            <Link to={`/invoices/${inv.id}`} className="btn btn-outline btn-sm" title="Details">
                              <Eye size={14} />
                            </Link>
                            {inv.status === 'draft' && (
                              <Link to={`/invoices/${inv.id}/edit`} className="btn btn-outline btn-sm" title="Bearbeiten">
                                <Edit2 size={14} />
                              </Link>
                            )}
                            <button
                              onClick={() => handleDownloadPdf(inv)}
                              className="btn btn-outline btn-sm"
                              title="PDF herunterladen"
                              disabled={busy}
                            >
                              <Download size={14} />
                            </button>
                            {inv.status === 'draft' && inv.customer?.email && (
                              <button
                                onClick={() => handleSend(inv)}
                                className="btn btn-outline btn-sm"
                                title="Versenden"
                                disabled={busy}
                              >
                                <Send size={14} />
                              </button>
                            )}
                            {inv.status !== 'paid' && (
                              <button
                                onClick={() => handleMarkPaid(inv)}
                                className="btn btn-outline btn-sm"
                                title="Als bezahlt markieren"
                                disabled={busy}
                              >
                                <CheckCircle2 size={14} />
                              </button>
                            )}
                            {inv.status === 'draft' && (
                              <button
                                onClick={() => handleDelete(inv)}
                                className="btn btn-danger btn-sm"
                                title="Löschen"
                                disabled={busy}
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-12">
                <FileText size={48} className="mx-auto mb-4 text-gray-400" />
                <h3 className="text-lg font-semibold mb-2">Keine Rechnungen gefunden</h3>
                <p className="text-gray-600 mb-6">
                  {searchTerm || statusFilter
                    ? 'Keine Treffer für die Filter.'
                    : 'Es wurde noch keine Rechnung erstellt.'}
                </p>
                <Link to="/invoices/new" className="btn btn-primary">
                  <Plus size={16} />
                  Erste Rechnung erstellen
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Invoices;
